import { For, Match, Show, Switch, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { ForgotPasswordScope } from 'mormaii-web-presentation/forgot-password'

import { AccountFrame, Alert, Field, Heading, PrimaryButton, TextButton } from '../../components/account'

import Css from './account.module.scss'

/** A ordem das etapas, que e tambem a ordem das marcas de progresso. */
const STEPS = ['email', 'code', 'password'] as const

/**
 * A recuperacao de senha, em tres etapas: e-mail, codigo, senha nova.
 *
 * A etapa e do SCOPE (`step`), e nao de estado local desta view - quem a conduz e o
 * presenter, que sabe o que o servidor respondeu. Aqui so se escolhe o que
 * desenhar.
 *
 * O codigo chega por e-mail e e DIGITADO; nao ha link no e-mail. Por isso a segunda
 * etapa tem um campo e um relogio, e nao uma tela de espera.
 */
export function ForgotPasswordView(props: ViewProps<ForgotPasswordScope>): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSubmit()
    }

    const buttonLabel = () =>
        props.scope.busy
            ? 'Enviando…'
            : props.scope.step === 'email'
              ? 'Enviar código'
              : props.scope.step === 'code'
                ? 'Confirmar código'
                : 'Redefinir senha'

    return (
        <AccountFrame>
            <Show
                when={!props.scope.done}
                fallback={
                    <div class={Css.done}>
                        <Heading subtitle="Sua nova senha foi redefinida e já pode ser usada para acessar sua conta.">
                            Senha redefinida
                        </Heading>
                        <PrimaryButton type="button" onClick={() => void props.scope.onGoToSignIn()}>
                            Ir para o login
                        </PrimaryButton>
                    </div>
                }
            >
                <Heading
                    subtitle={
                        props.scope.step === 'email'
                            ? 'Informe o e-mail da conta. Enviaremos um código de confirmação.'
                            : props.scope.step === 'code'
                              ? 'Digite o código que chegou no seu e-mail.'
                              : 'Escolha a nova senha da sua conta.'
                    }
                >
                    Esqueci minha senha
                </Heading>

                {/* Três marcas de etapa: dizer onde se está encurta a espera. */}
                <ol class={Css.progress} aria-label="Etapas">
                    <For each={STEPS}>
                        {(step, index) => (
                            <li
                                class={Css.progressStep}
                                classList={{ [Css.progressDone]: STEPS.indexOf(props.scope.step) >= index() }}
                                aria-current={props.scope.step === step ? 'step' : undefined}
                            />
                        )}
                    </For>
                </ol>

                <form class={Css.form} onSubmit={submit}>
                    <Alert message={props.scope.error} />

                    <Switch>
                        <Match when={props.scope.step === 'email'}>
                            <Field
                                label="E-mail"
                                type="email"
                                autocomplete="username"
                                value={props.scope.email}
                                onChange={(v) => props.scope.onEmailChanged(v)}
                            />
                        </Match>

                        <Match when={props.scope.step === 'code'}>
                            <Field
                                label="Código"
                                autocomplete="one-time-code"
                                value={props.scope.code}
                                onChange={(v) => props.scope.onCodeChanged(v)}
                                hint={
                                    props.scope.secondsLeft > 0
                                        ? `Tempo limite para confirmação: ${props.scope.timer}`
                                        : 'O código expirou. Peça um novo.'
                                }
                            />

                            <TextButton
                                class={`${Css.tucked} ${Css.toStart}`}
                                onClick={() => void props.scope.onResend()}
                            >
                                Reenviar código
                            </TextButton>
                        </Match>

                        <Match when={props.scope.step === 'password'}>
                            <Field
                                label="Nova senha"
                                type="password"
                                autocomplete="new-password"
                                value={props.scope.password}
                                onChange={(v) => props.scope.onPasswordChanged(v)}
                            />
                            <Field
                                label="Confirme a nova senha"
                                type="password"
                                autocomplete="new-password"
                                value={props.scope.confirmPassword}
                                onChange={(v) => props.scope.onConfirmPasswordChanged(v)}
                            />
                        </Match>
                    </Switch>

                    <PrimaryButton busy={props.scope.busy}>{buttonLabel()}</PrimaryButton>
                </form>

                <p class={Css.back}>
                    <TextButton onClick={() => void props.scope.onGoToSignIn()}>Voltar para o login</TextButton>
                </p>
            </Show>
        </AccountFrame>
    )
}
